import { useUnit } from 'effector-react';
import { MoreHorizontal, Pencil, ShoppingCart, Undo2, X, XCircle, SkipForward } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertIcon, AlertMessage } from '../../entities/alert';
import { $bars, RegimePills, loadBarsFx } from '../../entities/market';
import {
  PositionStatusPill,
  SymbolPill,
  cancelPositionFx,
  deleteFillFx,
  placesFor,
  skipLevelFx,
  triggeredLevels,
  updatePositionFx,
  type PositionDetail as Detail,
} from '../../entities/position';
import { closeDialogOpened } from '../../features/close-position';
import { confirmRequested } from '../../features/confirm';
import { editTargetOpened } from '../../features/edit-target';
import { fillDialogOpened } from '../../features/record-fill';
import type { Schemas } from '../../shared/api/client';
import {
  formatDate,
  formatDateTime,
  formatMonth,
  formatMoney,
  formatNumber,
  formatPercent,
  formatRelative,
  formatShares,
  parseDate,
  toNumber,
} from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import {
  Button,
  ChartCard,
  DataTable,
  IconButton,
  KpiTile,
  LineChart,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Pill,
  PlValue,
  Section,
  StatusDot,
  Textarea,
  Tooltip,
  type Column,
  type ReferenceLine,
} from '../../shared/ui';

export interface PositionDetailProps {
  detail: Detail;
  onClose?: () => void;
  /** The 360 px inspector: fewer ladder columns, icon-only row actions. */
  compact?: boolean;
}

export function PositionDetail({ detail, onClose, compact = false }: PositionDetailProps) {
  const { t } = useTranslation();
  const active = detail.status === 'open' || detail.status === 'target_reached';
  const firstOpenLevel =
    triggeredLevels(detail)[0] ?? detail.levels.find((l) => l.n > 0 && l.status === 'pending');
  return (
    <div className="flex flex-col">
      <header className="flex min-h-11 flex-wrap items-center gap-2 px-4 py-2">
        <SymbolPill symbol={detail.instrument.display_symbol} />
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{detail.instrument.name}</h2>
        <PositionStatusPill status={detail.status} triggered={triggeredLevels(detail).length} />
        {active && (
          <Menu>
            <Tooltip content={t('common.more')}>
              <MenuTrigger asChild>
                <IconButton label={t('common.more')}>
                  <MoreHorizontal />
                </IconButton>
              </MenuTrigger>
            </Tooltip>
            <MenuContent>
              {firstOpenLevel && (
                <MenuItem
                  icon={<ShoppingCart />}
                  shortcut={['F']}
                  onSelect={() => {
                    fillDialogOpened({ position: detail, level: firstOpenLevel.n });
                  }}
                >
                  {t('position.recordFill')}
                </MenuItem>
              )}
              <MenuItem
                icon={<Pencil />}
                onSelect={() => {
                  editTargetOpened(detail);
                }}
              >
                {t('position.editTarget')}
              </MenuItem>
              <MenuItem
                icon={<XCircle />}
                onSelect={() => {
                  closeDialogOpened(detail);
                }}
              >
                {t('position.close')}
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                danger
                icon={<X />}
                onSelect={() => {
                  confirmRequested({
                    title: t('position.cancelTitle'),
                    body: t('position.cancelBody'),
                    confirmLabel: t('position.cancel'),
                    danger: true,
                    onConfirm: () => cancelPositionFx(detail.id),
                  });
                }}
              >
                {t('position.cancel')}
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
        {onClose && (
          <IconButton label={t('common.close')} onClick={onClose}>
            <X />
          </IconButton>
        )}
      </header>
      <div className="space-y-3 px-4 pb-4">
        <Kpis detail={detail} />
        <LadderTable detail={detail} compact={compact} />
        <PriceChart detail={detail} />
      </div>
      <RegimeSection detail={detail} />
      <AlertsSection detail={detail} />
      {/* Keyed by position, so an unsaved draft never carries over to another one. */}
      <NotesSection key={detail.id} detail={detail} />
    </div>
  );
}

function Kpis({ detail }: { detail: Detail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const places = placesFor(detail);
  const holding = detail.holding;
  if (detail.realized) {
    const pl = toNumber(detail.realized.pl);
    return (
      <div className="grid grid-cols-2 gap-3">
        <KpiTile label={t('position.invested')} value={formatMoney(holding.cost, locale)} />
        <KpiTile
          label={t('position.proceeds')}
          value={formatMoney(detail.realized.proceeds, locale)}
        />
        <KpiTile
          label={t('position.pl')}
          tone={pl < 0 ? 'negative' : undefined}
          value={formatMoney(detail.realized.pl, locale, { signed: true })}
          detail={
            <PlValue value={pl}>
              {formatPercent(detail.realized.pl_pct, locale, { signed: true })}
            </PlValue>
          }
        />
        <KpiTile
          label={t('position.age')}
          value={t('position.sessions', { count: detail.age.sessions })}
        />
      </div>
    );
  }
  const valuation = detail.valuation;
  const pl = valuation ? toNumber(valuation.pl) : 0;
  return (
    <div className="grid grid-cols-2 gap-3">
      <KpiTile
        label={t('position.invested')}
        value={formatMoney(holding.cost, locale)}
        detail={t('position.sharesAt', {
          shares: formatShares(holding.shares, locale),
          average: formatMoney(holding.average, locale, { places }),
        })}
      />
      <KpiTile
        label={t('position.marketValue')}
        value={valuation ? formatMoney(valuation.market_value, locale) : '—'}
        detail={
          detail.quote
            ? t('position.lastPrice', {
                price: formatMoney(detail.quote.price, locale, { places }),
                time: formatRelative(detail.quote.ts, new Date(), locale),
              })
            : t('position.noQuote')
        }
      />
      <KpiTile
        label={t('position.pl')}
        tone={pl < 0 ? 'negative' : undefined}
        value={valuation ? formatMoney(valuation.pl, locale, { signed: true }) : '—'}
        detail={
          valuation ? (
            <PlValue value={pl}>
              {formatPercent(valuation.pl_pct, locale, { signed: true })}
            </PlValue>
          ) : undefined
        }
      />
      <KpiTile
        label={t('position.toTarget')}
        value={formatMoney(holding.target_price, locale, { places })}
        detail={
          valuation
            ? toNumber(valuation.to_target_pct) <= 0
              ? t('position.targetReached')
              : t('position.away', { distance: formatPercent(valuation.to_target_pct, locale) })
            : t('position.targetOf', { target: formatPercent(detail.target_pct, locale) })
        }
      />
    </div>
  );
}

const LEVEL_DOT = { triggered: 'warning', filled: 'good' } as const;

function LadderTable({ detail, compact }: { detail: Detail; compact: boolean }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const places = placesFor(detail);
  const active = detail.status === 'open' || detail.status === 'target_reached';
  const columns: Column<Schemas['LevelOut']>[] = [
    {
      id: 'n',
      header: t('ladder.level'),
      cell: (row) =>
        row.n === 0
          ? t('ladder.entry')
          : compact
            ? `L${String(row.n)}`
            : t('ladder.levelN', { n: row.n }),
    },
    {
      id: 'price',
      header: compact ? t('ladder.price') : t('ladder.trigger'),
      numeric: true,
      cell: (row) => formatMoney(row.planned_price, locale, { places }),
    },
    {
      id: 'shares',
      header: t('ladder.shares'),
      numeric: true,
      cell: (row) => formatShares(row.planned_shares, locale),
    },
    {
      id: 'cost',
      header: t('ladder.cost'),
      numeric: true,
      cell: (row) => formatMoney(row.planned_cost, locale),
    },
    {
      id: 'status',
      header: t('ladder.status'),
      cell: (row) => {
        const dot =
          row.status === 'triggered' || row.status === 'filled' ? LEVEL_DOT[row.status] : undefined;
        return (
          <Pill icon={dot ? <StatusDot status={dot} /> : undefined}>
            {t(`ladder.statuses.${row.status}`)}
          </Pill>
        );
      },
    },
    {
      id: 'fill',
      header: t('ladder.fill'),
      cell: (row) =>
        row.fill ? (
          <span className="tabular text-text-2">
            {formatMoney(row.fill.price, locale, { places })} ·{' '}
            {formatDate(row.fill.executed_at, locale)}
          </span>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      cell: (row) => {
        if (!active || row.n === 0) return null;
        if (row.status === 'pending' || row.status === 'triggered') {
          return (
            <span className="flex items-center justify-end gap-0.5">
              {compact ? (
                <Tooltip content={t('position.recordFill')}>
                  <IconButton
                    size="sm"
                    variant={row.status === 'triggered' ? 'secondary' : 'ghost'}
                    label={t('position.recordFillLevel', { n: row.n })}
                    onClick={() => {
                      fillDialogOpened({ position: detail, level: row.n });
                    }}
                  >
                    <ShoppingCart />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  size="sm"
                  variant={row.status === 'triggered' ? 'secondary' : 'ghost'}
                  onClick={() => {
                    fillDialogOpened({ position: detail, level: row.n });
                  }}
                >
                  {t('position.recordFillShort')}
                </Button>
              )}
              <Tooltip content={t('position.skip')}>
                <IconButton
                  size="sm"
                  label={t('position.skipLevel', { n: row.n })}
                  onClick={() => {
                    confirmRequested({
                      title: t('position.skipTitle', { n: row.n }),
                      body: t('position.skipBody'),
                      confirmLabel: t('position.skip'),
                      onConfirm: () => skipLevelFx({ id: detail.id, n: row.n }),
                    });
                  }}
                >
                  <SkipForward />
                </IconButton>
              </Tooltip>
            </span>
          );
        }
        if (row.status === 'filled' && row.fill) {
          const fillId = row.fill.id;
          return (
            <span className="flex justify-end">
              <Tooltip content={t('position.undoFill')}>
                <IconButton
                  size="sm"
                  label={t('position.undoFillLevel', { n: row.n })}
                  onClick={() => {
                    confirmRequested({
                      title: t('position.undoTitle', { n: row.n }),
                      body: t('position.undoBody'),
                      confirmLabel: t('position.undoFill'),
                      onConfirm: () => deleteFillFx({ id: detail.id, fillId }),
                    });
                  }}
                >
                  <Undo2 />
                </IconButton>
              </Tooltip>
            </span>
          );
        }
        return null;
      },
    },
  ];
  const visible = compact ? columns.filter((c) => c.id !== 'cost' && c.id !== 'fill') : columns;
  return (
    <section className="rounded-lg border border-border-subtle bg-bg-1">
      <h3 className="flex h-9 items-center px-3 text-xs font-medium text-text-2">
        {t('position.ladder')}
      </h3>
      <div className="overflow-x-auto px-1 pb-1">
        <DataTable
          dense
          caption={t('position.ladder')}
          columns={visible}
          rows={detail.levels}
          rowKey={(row) => row.n}
        />
      </div>
    </section>
  );
}

function PriceChart({ detail }: { detail: Detail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const places = placesFor(detail);
  const [bars, load] = useUnit([$bars, loadBarsFx]);
  const instrumentId = detail.instrument.id;
  useEffect(() => {
    void load(instrumentId).catch(() => undefined);
  }, [instrumentId, load]);
  const data = bars[instrumentId];
  if (!data?.length) return null;

  const points = data.map((b) => ({ x: parseDate(b.day), y: toNumber(b.close) }));
  const pendingNext = detail.levels.find((l) => l.n > 0 && l.status === 'pending');
  const lines: ReferenceLine[] = detail.levels
    .filter(
      (l) => l.n > 0 && (l.status === 'triggered' || l.status === 'filled' || l === pendingNext),
    )
    .map((l) => ({
      id: `level-${String(l.n)}`,
      y: toNumber(l.planned_price),
      label: `L${String(l.n)} ${formatMoney(l.planned_price, locale, { places })}`,
      kind: 'level' as const,
    }));
  const active = detail.status === 'open' || detail.status === 'target_reached';
  if (active) {
    lines.push({
      id: 'target',
      y: toNumber(detail.holding.target_price),
      label: `${t('ladder.target')} ${formatMoney(detail.holding.target_price, locale, { places })}`,
      kind: 'target' as const,
    });
  }
  const first = points[0]?.x.getTime() ?? 0;
  const markers = detail.fills
    .filter((f) => new Date(f.executed_at).getTime() >= first)
    .map((f) => ({
      id: `fill-${String(f.id)}`,
      x: new Date(f.executed_at),
      y: toNumber(f.price),
      label: f.side === 'buy' ? t('ladder.buy') : t('ladder.sell'),
    }));

  return (
    <ChartCard
      title={t('position.priceChart')}
      subtitle={t('position.sixMonths')}
      labels={{ viewAsTable: t('charts.viewAsTable'), viewAsChart: t('charts.viewAsChart') }}
      table={
        <DataTable
          dense
          caption={t('position.priceChart')}
          columns={[
            {
              id: 'day',
              header: t('charts.date'),
              cell: (row) => formatDate(row.day, locale, 'UTC'),
            },
            {
              id: 'close',
              header: t('charts.close'),
              numeric: true,
              cell: (row) => formatMoney(row.close, locale, { places }),
            },
          ]}
          rows={[...data].reverse()}
          rowKey={(row) => row.day}
        />
      }
    >
      <LineChart
        label={t('position.priceChartLabel', { symbol: detail.instrument.display_symbol })}
        series={[{ id: 'close', label: t('charts.close'), color: 1, points }]}
        references={lines}
        markers={markers}
        height={200}
        formatY={(v) => formatMoney(v, locale, { places: 0 })}
        formatXTick={(d) => formatMonth(d, locale)}
        formatXLong={(d) => formatDate(d, locale, 'UTC')}
      />
    </ChartCard>
  );
}

function RegimeSection({ detail }: { detail: Detail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { age } = detail;
  return (
    <Section title={t('position.regime')}>
      <Row label={t('position.atEntry')}>
        {detail.regime_at_entry ? <RegimePills regime={detail.regime_at_entry} /> : '—'}
      </Row>
      <Row label={t('position.now')}>
        {detail.regime_now ? <RegimePills regime={detail.regime_now} /> : '—'}
      </Row>
      <Row label={t('position.age')}>
        <span className="tabular text-sm">
          {t('position.ageLine', {
            day: formatNumber(age.sessions, locale),
            median: age.median === null ? '—' : formatNumber(age.median, locale),
            p90: age.p90 === null ? '—' : formatNumber(age.p90, locale),
          })}
        </span>
      </Row>
      <p className="text-xs text-text-3">{t('position.regimeCaveat')}</p>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center gap-x-3">
      <span className="text-xs text-text-3">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

function AlertsSection({ detail }: { detail: Detail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (detail.alerts.length === 0) return null;
  return (
    <Section title={t('position.alerts')}>
      <ul className="space-y-1">
        {detail.alerts.map((alert) => (
          <li key={alert.id} className="flex items-center gap-2 text-sm">
            <AlertIcon alert={alert} />
            <span className="min-w-0 flex-1 truncate text-text-2">
              <AlertMessage alert={alert} />
            </span>
            <Tooltip content={formatDateTime(alert.created_at, locale)}>
              <span className="text-xs text-text-3">
                {formatRelative(alert.created_at, new Date(), locale)}
              </span>
            </Tooltip>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function NotesSection({ detail }: { detail: Detail }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(detail.notes);
  const save = useUnit(updatePositionFx);
  const reverting = useRef(false);
  const [shown, setShown] = useState(detail.notes);
  if (shown !== detail.notes) {
    setShown(detail.notes);
    setValue(detail.notes);
  }
  return (
    <Section title={t('position.notes')}>
      <Textarea
        aria-label={t('position.notes')}
        value={value}
        placeholder={t('position.notesPlaceholder')}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            reverting.current = true;
            setValue(detail.notes);
            event.currentTarget.blur();
          }
        }}
        onBlur={() => {
          if (reverting.current) {
            reverting.current = false;
            return;
          }
          if (value !== detail.notes)
            void save({ id: detail.id, patch: { notes: value } }).catch(() => undefined);
        }}
      />
    </Section>
  );
}
